import { useLingui } from "@lingui/react";
import type { BusAd, CaravanAd, MobileHomeAd } from "@mobility-item-page/types";
import { formatNumber } from "@mobility-item-page/utilities";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import FieldValuePair from "../FieldValuePair.tsx";
import type { SpecProps } from "./types.ts";

export const Weight = ({ weight }: { weight?: number }) => {
  if (!weight) {
    return null;
  }
  return <PrimitiveWeight weight={weight} />;
};

export const RVWeight = ({ weight }: SpecProps<"weight", CaravanAd | MobileHomeAd>) => {
  if (!weight) {
    return null;
  }
  return (
    <>
      {weight.total && <TotalWeight weight={weight.total} />}
      {weight.net && <NetWeight weight={weight.net} />}
      {weight.total && weight.net && weight.total - weight.net >= 0 && <Payload weight={weight.total - weight.net} />}
    </>
  );
};

export const BusWeight = ({ weight }: SpecProps<"weight", BusAd>) => {
  if (!weight) {
    return null;
  }
  return (
    <>
      {weight.main && <PrimitiveWeight weight={weight.main} />}
      {weight.cargo && <Payload weight={weight.cargo} />}
    </>
  );
};

const PrimitiveWeight = ({ weight }: { weight?: number }) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  if (weight == null || weight === 0) {
    return null;
  }

  const field = i18n.t({
    id: "itemPageLayout.keyInfo.weight",
    message: "Weight",
    comment: "Field for 'weight' in ad specification",
  });

  const value = i18n.t({
    id: "itemPageLayout.common.weight.postfix",
    message: "{weight} kg",
    comment: "Weight incl. postfix",
    values: {
      weight: formatNumber(weight, locale),
    },
  });
  return <FieldValuePair field={field} value={value} />;
};

export const TotalWeight = ({ weight }: { weight: number }) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  if (weight == null || weight === 0) {
    return null;
  }

  const field = i18n.t({
    id: "itemPageLayout.keyInfo.totalWeight",
    message: "Total weight",
    comment: "Field for 'weight' in ad specification",
  });

  const value = i18n.t({
    id: "itemPageLayout.common.weight.postfix",
    message: "{weight} kg",
    comment: "Weight incl. postfix",
    values: {
      weight: formatNumber(weight, locale),
    },
  });
  return <FieldValuePair field={field} value={value} />;
};

const NetWeight = ({ weight }: { weight: number }) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  if (weight == null || weight === 0) {
    return null;
  }

  const field = i18n.t({
    id: "itemPageLayout.common.netWeight",
    message: "Net weight",
    comment: "Field for 'weight' in ad specification",
  });

  const value = i18n.t({
    id: "itemPageLayout.common.weight.postfix",
    message: "{weight} kg",
    comment: "Weight incl. postfix",
    values: {
      weight: formatNumber(weight, locale),
    },
  });
  return <FieldValuePair field={field} value={value} />;
};

export const Payload = ({ weight }: { weight: number }) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  if (weight == null) {
    return null;
  }

  const field = i18n.t({
    id: "itemPageLayout.keyInfo.Payload",
    message: "Payload",
    comment: "Field for 'payload' in ad specification",
  });

  const value = i18n.t({
    id: "itemPageLayout.common.weight.postfix",
    message: "{weight} kg",
    comment: "Weight incl. postfix",
    values: {
      weight: formatNumber(weight, locale),
    },
  });
  return <FieldValuePair field={field} value={value} />;
};
